import { useState, useCallback, useEffect } from 'react';
import { TourProgress } from '../types';

const STORAGE_KEY = 'showme-progress';

const defaultProgress: TourProgress = {
  understoodFeatures: [],
  lastTourDate: null,
};

function loadProgress(): TourProgress {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as TourProgress;
    }
  } catch (e) {
    console.warn('[ShowMe] Failed to load progress from localStorage', e);
  }
  return defaultProgress;
}

function saveProgress(progress: TourProgress): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
  } catch (e) {
    console.warn('[ShowMe] Failed to save progress to localStorage', e);
  }
}

export function useTourProgress() {
  const [progress, setProgress] = useState<TourProgress>(loadProgress);

  // Sync with localStorage on mount
  useEffect(() => {
    setProgress(loadProgress());
  }, []);

  const markAsUnderstood = useCallback((scenarioId: string) => {
    setProgress((prev) => {
      if (prev.understoodFeatures.includes(scenarioId)) {
        return prev;
      }
      const updated: TourProgress = {
        understoodFeatures: [...prev.understoodFeatures, scenarioId],
        lastTourDate: new Date().toISOString(),
      };
      saveProgress(updated);
      return updated;
    });
  }, []);

  const hasUnderstood = useCallback((scenarioId: string): boolean => {
    return progress.understoodFeatures.includes(scenarioId);
  }, [progress.understoodFeatures]);

  const clearProgress = useCallback(() => {
    setProgress(defaultProgress);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return {
    progress,
    markAsUnderstood,
    hasUnderstood,
    clearProgress,
  };
}
